import mediaQuery from '../../../helpers/mediaQuery';
import isTouchSupported from '../../../helpers/isTouchSupported';
import { isIOS } from '../../../helpers/browserHelpers';
import { lockBodyScroll, unlockBodyScroll } from '../../../helpers/bodyScrollLock';

import { addThemeClassNames, removeThemeClassNames } from '../../../helpers/themesHelpers';

const MAIN_MENU_ITEM = '.main-menu__item';
const IS_HOVER = '_is-hover';
const IS_OPEN = '_is-open';
const HIDE = 'hide-visibility';
const NAV_HIDE_CLASSNAME = 'hide-up-to-lg';

let hoverTimeout;
let openedMenu: HTMLElement | null;

const navElement = document.querySelector<HTMLElement>('.nav');
const promoBarElement = document.querySelector<HTMLElement>('.page-header-promo-bar');
const logoElement = document.querySelector<HTMLElement>('.page-header__logo');
const menuOverlayElement = document.querySelector<HTMLDivElement>('.page-header-backdrop-overlay');
const menuWrapperElement = document.querySelector<HTMLDivElement>('.nav__wrapper');
const menuTogglerElement = document.querySelector<HTMLButtonElement>('.page-header__menu-toggler');
const mainMenuElement = document.querySelector<HTMLDivElement>('.main-menu');
const secondMenuElement = document.querySelector<HTMLDivElement>('.second-menu');

function hideOverlay(): void {
  menuOverlayElement?.classList.add(HIDE);
}

function showOverlay(): void {
  menuOverlayElement?.classList.remove(HIDE);
}

function menuOverlayCloseDesktopHandler(): void {
  hideOverlay();
  closeOpenedMenuItems();
}

function openMenuDesktop(hoveredMenu: HTMLElement): void {
  openedMenu?.classList.remove(IS_HOVER);
  hoveredMenu.classList.add(IS_HOVER);
  openedMenu = hoveredMenu;

  const secondMenu = hoveredMenu.querySelector('.second-menu') as HTMLElement;
  const menuHeight = secondMenu?.getBoundingClientRect().height || 0;
  const navHeight = navElement && getComputedStyle(navElement).getPropertyValue('--nav-height');
  try {
    navElement?.style.setProperty('height', `calc(${menuHeight}px + ${navHeight})`);
  } catch (error) {
    // Note: try catch block is used to fix IE11 not being able to set height property as calc(...)
    const { message } = error as Error;
    console.error(message);
  }
  showOverlay();
  removeThemeClassNames();
}

function closeMenuDesktop(): void {
  clearTimeout(hoverTimeout);
  hideOverlay();
  openedMenu?.classList.remove(IS_HOVER);
  openedMenu = null;
  navElement?.style.removeProperty('height');
  addThemeClassNames();
}

function menuHoverHandler(e: MouseEvent): void {
  clearTimeout(hoverTimeout);
  hoverTimeout = setTimeout(() => {
    const hoveredMenu = (e.target as HTMLElement).closest(MAIN_MENU_ITEM) as HTMLElement;
    if (hoveredMenu) {
      const hasNoSubMenu = !hoveredMenu.classList.contains('main-menu__item--has-children');
      if (hasNoSubMenu) {
        closeMenuDesktop();
        return;
      }
      const isAlreadyOpen = openedMenu === hoveredMenu;
      if (!isAlreadyOpen) {
        openMenuDesktop(hoveredMenu);
      }
    }
  }, 100);
}

function handleClickOutside(e: Event): void {
  const isOutside = !(e.target as Element).closest('[data-target-menu]');
  if (isOutside) {
    menuTogglerHandler();
  }
}

function closeOpenedMenuItems(): void {
  const openedMenuItemElements = document.querySelectorAll(
    '.main-menu__item--has-children, .second-menu__item--has-children'
  );
  [...openedMenuItemElements].forEach((element) => element.classList.remove(IS_OPEN));
}

function openMenuTabletMobile(): void {
  menuTogglerElement?.setAttribute('aria-pressed', 'true');
  menuTogglerElement?.classList.add('page-header__menu-toggler--opened');
  navElement?.classList.remove(NAV_HIDE_CLASSNAME);
  showOverlay();
  setMenuDistanceToTheTopOfThePage(mediaQuery.isXLargeUp());
  lockBodyScroll();
  closeOpenedMenuItems();
  document.addEventListener('click', handleClickOutside, { capture: true });

  removeThemeClassNames();
}

function closeMenuTabletMobile(): void {
  menuTogglerElement?.setAttribute('aria-pressed', 'false');
  menuTogglerElement?.classList.remove('page-header__menu-toggler--opened');
  navElement?.classList.add(NAV_HIDE_CLASSNAME);
  hideOverlay();
  setMenuDistanceToTheTopOfThePage(mediaQuery.isXLargeUp());
  unlockBodyScroll();
  closeOpenedMenuItems();
  document.removeEventListener('click', handleClickOutside, { capture: true });
  addThemeClassNames();
}

function menuTogglerHandler(): void {
  const isOpened = menuTogglerElement?.getAttribute('aria-pressed') === 'true';
  if (isOpened) {
    closeMenuTabletMobile();
  } else {
    openMenuTabletMobile();
  }
}

function menuClickHandler(e: MouseEvent): void {
  e.stopPropagation();
  const clickedMenuItem = getClickedMenuItem(e.target as Element | null);
  if (clickedMenuItem) {
    e.preventDefault();
    menuItemToggler(clickedMenuItem as HTMLElement);
  }

  function menuItemToggler(menuItem: HTMLElement): void {
    menuItem.classList[menuItem.classList.contains(IS_OPEN) ? 'remove' : 'add'](IS_OPEN);
    if (menuItem.classList.contains('main-menu__item--has-children')) {
      const clickedSecondMenuSiblings = menuItem.parentNode ? menuItem.parentNode.children : [];
      [...clickedSecondMenuSiblings].forEach(
        (sibling) =>
          sibling !== clickedMenuItem &&
          sibling.classList.contains('main-menu__item--has-children') &&
          sibling.classList.remove(IS_OPEN)
      );
    }
    if (menuItem.classList.contains('second-menu__item--has-children')) {
      const clickedSecondMenuSiblings = menuItem.parentNode ? menuItem.parentNode.children : [];
      [...clickedSecondMenuSiblings].forEach(
        (sibling) =>
          sibling !== clickedMenuItem &&
          sibling.classList.contains('second-menu__item--has-children') &&
          sibling.classList.remove(IS_OPEN)
      );
    }
  }

  function getClickedMenuItem(clickedElement: Element | null): Element | null | undefined {
    const elementWithClassName = (className): boolean =>
      clickedElement?.classList.contains(className) ||
      (clickedElement?.parentNode as HTMLElement)?.classList.contains(className);
    const mainMenuItem = ['main-menu__link', mediaQuery.isSmallOnly() ? 'second-menu__back' : '']
      .filter(Boolean)
      .find(elementWithClassName)
      ? clickedElement?.closest('.main-menu__item--has-children')
      : null;
    const secondMenuItem = ['second-menu__link'].find(elementWithClassName)
      ? clickedElement?.closest('.second-menu__item--has-children')
      : null;
    return secondMenuItem || mainMenuItem;
  }
}

function removeRubberEffect(e: Event): void {
  if (e.type === 'touchmove') {
    e.stopPropagation();
  }
  if (e.type === 'touchstart') {
    const element = e.currentTarget as HTMLElement;
    const top = element.scrollTop;
    const totalScroll = element.scrollHeight;
    const currentScroll = top + element.offsetHeight;

    if (top === 0) {
      element.scrollTop = 1;
    }

    if (currentScroll === totalScroll) {
      element.scrollTop = top - 1;
    }
  }
}

function changeThirdMenuBrandPosition(): void {
  // TODO: Talk to Beaverbrooks and remove this nasty hardcoded piece
  // Third menu is hidden on Mobile/Tablet, but displayed on Desktop to span "By Brand" category to 2 columns.
  // Usually the last column would appear with an empty title which makes a feel like it's a continuation of the previous column.
  // This is a legacy hack to avoid blank accordion second-menu title on mobile, instead gluing it with "By Brand" menu.
  const splittedOnDesktopNavList = ['by brand', 'by collection'];

  [...document.querySelectorAll('.main-menu__item--has-children')].forEach((menuItem) => {
    [...menuItem.querySelectorAll('.second-menu__item--has-children')].forEach((secondMenu) => {
      const hasEmptyTitle = secondMenu.querySelector('.second-menu__link--title-empty');
      if (hasEmptyTitle) {
        const thirdMenuList = secondMenu.querySelector('.third-menu')?.cloneNode(true) as HTMLDivElement;
        const splittedNavCategory = [...menuItem.querySelectorAll('.second-menu__link--title')].find(
          (title) => title.textContent && splittedOnDesktopNavList.includes(title.textContent.trim().toLowerCase())
        );
        if (splittedNavCategory && thirdMenuList) {
          thirdMenuList.classList.add('hide-lg-up');
          splittedNavCategory.parentNode?.appendChild(thirdMenuList);
        }
      }
    });
  });
}

function menuTouchHandler(e: TouchEvent): void {
  if (e.touches.length !== 1) return;

  e.stopPropagation();

  const touchedMenu = (e.target as HTMLElement).closest(MAIN_MENU_ITEM) as HTMLElement;
  if (!touchedMenu) {
    return;
  }

  if (!touchedMenu.classList.contains(IS_HOVER) && mediaQuery.isXLargeUp()) {
    e.preventDefault();

    openMenuDesktop(touchedMenu);
    document.addEventListener('touchstart', closeOnOutsideTouch, { passive: false });
  }
}

function closeOnOutsideTouch(): void {
  closeMenuDesktop();
  document.removeEventListener('touchstart', closeOnOutsideTouch);
}

function setMenuDistanceToTheTopOfThePage(isXLargeUp: boolean): void {
  if (isXLargeUp) {
    navElement?.style.removeProperty('height');
    menuWrapperElement?.style.removeProperty('height');
  } else {
    const logoHeight = logoElement?.offsetHeight || 0;
    const promoBarHeight = promoBarElement?.offsetHeight || 0;
    const headerHeight = logoHeight + promoBarHeight;

    navElement?.style.setProperty('height', `calc(${window.innerHeight}px - ${headerHeight}px)`);
    menuWrapperElement?.style.setProperty('height', `calc(${window.innerHeight}px - ${headerHeight}px)`);
  }
}

const initMobileTabletNavigationEvents = (
  () =>
  (isXLargeUp: boolean): void => {
    if (isXLargeUp) {
      menuTogglerElement?.removeEventListener('click', menuTogglerHandler);
      mainMenuElement?.removeEventListener('click', menuClickHandler);
      if (isIOS()) {
        secondMenuElement?.removeEventListener('touchstart', removeRubberEffect);
        secondMenuElement?.removeEventListener('touchmove', removeRubberEffect);
      }
    } else {
      menuTogglerElement?.addEventListener('click', menuTogglerHandler);
      mainMenuElement?.addEventListener('click', menuClickHandler);
      if (isIOS()) {
        secondMenuElement?.addEventListener('touchstart', removeRubberEffect);
        secondMenuElement?.addEventListener('touchmove', removeRubberEffect);
      }
    }
  }
)();

const initDesktopNavigationEvents = (isXLargeUp: boolean): void => {
  if (isXLargeUp) {
    navElement?.addEventListener('mousemove', menuHoverHandler);
    navElement?.addEventListener('mouseleave', closeMenuDesktop);
    if (isTouchSupported()) {
      navElement?.addEventListener('touchstart', menuTouchHandler);
    }
  } else {
    navElement?.removeEventListener('mousemove', menuHoverHandler);
    navElement?.removeEventListener('mouseleave', closeMenuDesktop);
    if (isTouchSupported()) {
      navElement?.addEventListener('touchstart', menuTouchHandler);
    }
  }
};

function cleanUpOnViewportResize(isXLargeUp: boolean): void {
  const isMobileTabletMenuOpened = menuTogglerElement?.getAttribute('aria-pressed') === 'true';
  if (isXLargeUp && isMobileTabletMenuOpened) {
    menuTogglerHandler();
  } else {
    menuOverlayCloseDesktopHandler();
  }
}

function initNavigationEvents(): void {
  let currentIsXLargeUp = mediaQuery.isXLargeUp();

  changeThirdMenuBrandPosition();

  setMenuDistanceToTheTopOfThePage(currentIsXLargeUp);
  initMobileTabletNavigationEvents(currentIsXLargeUp);
  initDesktopNavigationEvents(currentIsXLargeUp);

  window.addEventListener('resize', () => {
    const newIsXLargeUp = mediaQuery.isXLargeUp();
    setMenuDistanceToTheTopOfThePage(newIsXLargeUp);
    if (currentIsXLargeUp !== newIsXLargeUp) {
      currentIsXLargeUp = newIsXLargeUp;
      initMobileTabletNavigationEvents(currentIsXLargeUp);
      initDesktopNavigationEvents(currentIsXLargeUp);
      cleanUpOnViewportResize(currentIsXLargeUp);
    }
  });
}

export default initNavigationEvents;
